<div
    class="rounded-lg p-1 w-full flex flex-col transition-colors"
    [ngClass]="{
        'bg-error !bg-opacity-40': editMode && markedForDelete,
        'bg-warning !bg-opacity-40': editMode && validationError && !markedForDelete,
        'bg-success !bg-opacity-20': editMode && markedForUpsert && !markedForDelete && !validationError
    }"
>
    <div class="min-h-[2.5rem] flex flex-row gap-5 items-center justify-between">
        <!-- Attribute description and name -->
        <div class="flex-1 flex flex-row items-center gap-5">
            <div class="flex flex-col justify-center">
                <div class="flex flex-row gap-2 items-center" *ngIf="!hideTitleAndValueType">
                    <span
                        class="font-normal text-base"
                        [ngClass]="{
                            'font-bold text-iota-required-attribute': isRequired,
                            'opacity-50': !attributeExists && !newlyCreated,
                            'text-[#A26100]': isDeprecated
                        }"
                        >{{ attributeDescription }} <span *ngIf="isRequired">*</span></span
                    >
                    <span class="font-normal text-base text-error" *ngIf="wouldOverrideParentButCantBeOverridden" i18n>
                        This attribute has already been set at product master data and cannot be overwritten.
                    </span>
                    <div>
                        <iota-icon
                            icon="information-circle"
                            [matTooltip]="attributeTooltip"
                            matTooltipClass="bg-iota-tooltip-bg whitespace-pre-line"
                            *ngIf="!!attributeTooltip"
                        ></iota-icon>
                    </div>

                    <div>
                        <iota-icon
                            icon="exclamation-triangle"
                            matTooltip="Discontinued attribute EOL"
                            i18n-matTooltip
                            matTooltipClass="bg-[#F39200] whitespace-pre-line"
                            class="text-[#F39200]"
                            *ngIf="isDeprecated"
                        ></iota-icon>
                    </div>
                </div>
                <span
                    class="text-xs"
                    [ngClass]="{ 'opacity-50': !attributeExists && !newlyCreated, 'text-[#A26100]': isDeprecated }"
                    *ngIf="!hideTitleAndValueType && showRealAttributeName"
                    >{{ iotaConfigAttribute?.name }}</span
                >
            </div>

            <div *ngIf="hideTitleAndValueType" class="flex-1"></div>
            <div
                class="h-6 w-6 bg-accent flex items-center justify-center rounded-full"
                [matTooltip]="isOverride ? overridesOtherAttributeTooltip : parentIdIsThisParentIdTooltip"
                matTooltipClass="bg-iota-tooltip-bg whitespace-pre-line"
                *ngIf="showEntitySpecificTooltip"
            >
                <iota-icon class="text-white" icon="adjustments-horizontal"></iota-icon>
            </div>
        </div>

        <!-- Inputs for the various attribute-types -->
        <ng-container *ngIf="attributeExists || newlyCreated">
            <div
                class="flex-1 max-w-lg flex flex-row items-center justify-end gap-2"
                *ngIf="stringValue !== undefined && !iotaConfigAttribute?.possibleValues"
            >
                <input
                    type="text"
                    class="flex-1 input input-sm input-bordered"
                    [(ngModel)]="stringValue"
                    [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                    (keyup)="checkIfValueHasChangedAndUpdateMark()"
                />
                <select
                    [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                    class="select select-sm select-bordered"
                    (change)="changeLocale($event)"
                >
                    <option *ngFor="let localeOption of possibleLocales" [ngValue]="localeOption" [selected]="localeOption === locale">
                        {{ localeOption }}
                    </option>
                </select>
            </div>
            <select
                class="select select-sm select-bordered"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                [(ngModel)]="stringValue"
                *ngIf="stringValue !== undefined && attributePossibleValuesWithLabel"
                (change)="checkIfValueHasChangedAndUpdateMark()"
            >
                <option *ngFor="let possibleValueWithLabel of attributePossibleValuesWithLabel" [value]="possibleValueWithLabel?.value">
                    <span *ngIf="!possibleValueWithLabel?.label">{{ possibleValueWithLabel?.value }}</span>
                    <span *ngIf="possibleValueWithLabel?.label">{{ possibleValueWithLabel?.label }}</span>
                    <span *ngIf="possibleValueWithLabel?.label && showRealAttributeName">&nbsp;[{{ possibleValueWithLabel?.value }}]</span>
                </option>
            </select>

            <input
                *ngIf="uuidValue !== undefined"
                type="text"
                class="flex-1 input input-sm input-bordered"
                [(ngModel)]="uuidValue"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (keyup)="checkIfValueHasChangedAndUpdateMark()" />
            <input
                *ngIf="intValue !== undefined"
                pattern="\d*"
                type="number"
                class="input input-sm input-bordered"
                [(ngModel)]="intValue"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (change)="checkIfValueHasChangedAndUpdateMark()"
                (keyup)="checkIfValueHasChangedAndUpdateMark()" />
            <input
                *ngIf="numberValue !== undefined"
                type="number"
                class="input input-sm input-bordered"
                [(ngModel)]="numberValue"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (change)="checkIfValueHasChangedAndUpdateMark()"
                (keyup)="checkIfValueHasChangedAndUpdateMark()" />
            <input
                *ngIf="booleanValue !== undefined"
                type="checkbox"
                class="toggle toggle-primary"
                [(ngModel)]="booleanValue"
                [disabled]="!canBeDeletedOrModified || !editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (change)="checkIfValueHasChangedAndUpdateMark()" />
            <input
                *ngIf="dateValue !== undefined"
                type="date"
                class="input input-sm input-bordered"
                [(ngModel)]="dateValue"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (change)="checkIfValueHasChangedAndUpdateMark()"
                (keyup)="checkIfValueHasChangedAndUpdateMark()" />
            <input
                *ngIf="datetimeValue !== undefined"
                type="datetime-local"
                class="input input-sm input-bordered"
                [(ngModel)]="datetimeValue"
                [disabled]="!editMode || markedForDelete || wouldOverrideParentButCantBeOverridden"
                (change)="checkIfValueHasChangedAndUpdateMark()"
                (keyup)="checkIfValueHasChangedAndUpdateMark()" />
            <span *ngIf="!!attributeUnit">{{ attributeUnit }}</span>

            <!-- Buttons when editing -->
            <div class="flex flex-row items-center gap-2" *ngIf="editMode">
                <button
                    i18n-matTooltip
                    matTooltip="Reset"
                    matTooltipClass="bg-iota-tooltip-bg"
                    class="btn btn-sm btn-accent"
                    [disabled]="!valueChanged || markedForDelete || wouldOverrideParentButCantBeOverridden"
                    (click)="resetValue()"
                    *ngIf="attributeExists && !newlyCreated"
                >
                    <iota-icon icon="arrow-uturn-left" size="1.2"></iota-icon>
                </button>

                <button
                    i18n-matTooltip
                    matTooltip="Delete"
                    matTooltipClass="bg-iota-tooltip-bg"
                    class="btn btn-sm btn-error"
                    [disabled]="markedForDelete || (!attributeExists && !markedForUpsert)"
                    (click)="markForDelete()"
                    *ngIf="canBeDeletedOrModified && !markedForDelete && (!attributeExists || parentId === previousAttribute?.parentSkId)"
                >
                    <iota-icon icon="trash" size="1.2"></iota-icon>
                </button>
                <button
                    i18n-matTooltip
                    matTooltip="Restore"
                    matTooltipClass="bg-iota-tooltip-bg"
                    class="btn btn-sm btn-primary"
                    (click)="undoMarkForDelete()"
                    *ngIf="markedForDelete"
                >
                    <iota-icon icon="arrow-path" size="1.2"></iota-icon>
                </button></div
        ></ng-container>

        <!-- Add-button if attribute doesn't exist yet -->
        <button
            i18n-matTooltip
            matTooltip="Add"
            matTooltipClass="bg-iota-tooltip-bg"
            class="btn btn-sm btn-primary"
            (click)="createAttribute()"
            *ngIf="!attributeExists && !newlyCreated && editMode"
        >
            <iota-icon icon="plus" size="1.2"></iota-icon>
        </button>
    </div>

    <span class="text-xs text-error self-end pr-1" *ngIf="validationError && !markedForDelete">{{ validationError }}</span>
</div>
